/**
 * ============================================================================
 * CONFIGURAZIONE: tourSteps.ts
 * ============================================================================
 * 
 * Configurazione centralizzata di tutti i tour guidati dell'applicazione.
 * Ogni tour è definito come un array di DriveStep che possono essere
 * avviati dal TourContext tramite startTour('nomeDelTour').
 * 
 * CONVENZIONE SELETTORI:
 * - Usare sempre [data-tour-id="..."] come selettore
 * - I data-tour-id devono essere semantici e in inglese (es. btn-save-course)
 * 
 * TOUR DISPONIBILI:
 * - dashboard: Tour di benvenuto alla Dashboard
 * - dashboard-mobile: Versione compatta per mobile
 * - course: Tour base navigazione corso
 * - course-lesson: Tour lezione (con step condizionali per istruttori)
 * - admin: Tour area amministrazione
 * 
 * NUOVE FUNZIONALITÀ (v1.1.x):
 * - White-label multi-sport: testi adattivi via isLni
 * - Favicon dinamica: ogni tenant ha la propria favicon nel browser tab
 * - Export documentazione PDF: rotta /admin/export-docs (data-tour-id="admin-export-docs")
 * 
 * @author CCFN Development Team
 * @version 2.1.0
 * @since 2026-02-19
 * ============================================================================
 */

import { TourStep } from "@/contexts/TourContext";

// ============================================================================
// TOUR: Dashboard - Primo accesso
// ============================================================================

/**
 * Tour di benvenuto alla Dashboard
 * Avviato automaticamente al primo accesso dell'utente
 */
export const DASHBOARD_TOUR: TourStep[] = [
  {
    // Step 1: Benvenuto centrale (senza elemento specifico)
    element: '[data-tour-id="dashboard-welcome"]',
    popover: {
      title: 'Benvenuto sulla piattaforma! 👋',
      description: 'Questa è la tua area personale. Ti guideremo alla scoperta delle funzionalità principali.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    // Step 2: Navbar / Menu principale
    element: '[data-tour-id="navbar"]',
    popover: {
      title: 'Menu di navigazione',
      description: 'Qui trovi il menu principale per accedere a tutte le sezioni: Corsi, Glossario, Forum e molto altro.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    // Step 3: Area statistiche
    element: '[data-tour-id="dashboard-stats"]',
    popover: {
      title: 'Le tue statistiche',
      description: 'Questa sezione mostra un riepilogo dei tuoi progressi: corsi iscritti, completati, ore di studio e percentuale di completamento.',
      side: 'top',
      align: 'center',
    },
  },
  {
    // Step 4: Area corsi
    element: '[data-tour-id="dashboard-my-courses"]',
    popover: {
      title: 'I tuoi corsi',
      description: 'Qui trovi tutti i corsi a cui sei iscritto. Puoi monitorare i progressi e accedere rapidamente ai contenuti.',
      side: 'top',
      align: 'center',
    },
  },
  {
    // Step 5: Profilo utente (nella navbar)
    element: '[data-tour-id="nav-user-info"]',
    popover: {
      title: 'Il tuo profilo',
      description: 'Qui puoi vedere il tuo account e il ruolo assegnato. Clicca su "Pannello" per gestire i tuoi dati personali.',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ============================================================================
// TOUR: Dashboard - Versione compatta per mobile
// ============================================================================

/**
 * Tour di benvenuto ottimizzato per dispositivi mobili
 * Con meno step e focus sulle azioni principali
 */
export const DASHBOARD_TOUR_MOBILE: TourStep[] = [
  {
    element: '[data-tour-id="dashboard-welcome"]',
    popover: {
      title: 'Benvenuto! 👋',
      description: 'Questa è la tua dashboard personale. Esploriamo insieme le funzionalità.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour-id="dashboard-stats"]',
    popover: {
      title: 'Le tue statistiche',
      description: 'Monitora i tuoi progressi: corsi, ore di studio e completamento.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-tour-id="dashboard-my-courses"]',
    popover: {
      title: 'I tuoi corsi',
      description: 'Accedi rapidamente ai corsi a cui sei iscritto.',
      side: 'top',
      align: 'center',
    },
  },
];

// ============================================================================
// TOUR: Corso - Navigazione contenuti
// ============================================================================

/**
 * Tour per la navigazione dei contenuti di un corso
 * Disponibile dalla pagina di dettaglio corso
 */
export const COURSE_TOUR: TourStep[] = [
  {
    element: '[data-tour-id="course-header"]',
    popover: {
      title: 'Dettagli del corso',
      description: 'Qui trovi le informazioni principali del corso: titolo, descrizione e metadati.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour-id="course-modules"]',
    popover: {
      title: 'Moduli del corso',
      description: 'Il corso è organizzato in moduli. Clicca su un modulo per espandere le sezioni e le pagine.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour-id="course-content"]',
    popover: {
      title: 'Contenuto della lezione',
      description: 'Qui viene visualizzato il contenuto della pagina selezionata. Scorri per leggere tutto il materiale.',
      side: 'left',
      align: 'start',
    },
  },
];

// ============================================================================
// TOUR: Lezione - Navigazione dettagliata (Nuovo v0.7.71)
// ============================================================================

/**
 * Step base del tour lezione, visibili a TUTTI gli utenti.
 * Copre navigazione gerarchica e domande di controllo.
 */
export const COURSE_LESSON_STEPS_BASE: TourStep[] = [
  {
    // Step 1: Navigazione gerarchica (Sidebar)
    element: '[data-tour-id="course-sidebar"]',
    popover: {
      title: 'Navigazione Gerarchica 🧭',
      description: 'Ora puoi esplorare le lezioni in profondità. Usa questo pannello per muoverti tra Moduli, Sezioni, Pagine e le nuove Sottopagine (es. 1.0.0.1). La struttura riflette fedelmente i contenuti didattici.',
      side: 'right',
      align: 'start',
    },
  },
  {
    // Step 4: Risorse e Quiz
    element: '[data-tour-id="course-control-questions"]',
    popover: {
      title: 'Domande di Controllo & Risorse 📝',
      description: 'Qui trovi le domande di autovalutazione e i materiali associati a questa specifica pagina. Rispondi per verificare la tua comprensione e attivare il ripasso spaziato.',
      side: 'top',
      align: 'center',
    },
  },
];

/**
 * Step aggiuntivi del tour lezione, visibili SOLO agli ISTRUTTORI.
 * Copre Modalità Presentazione e Speaker View.
 */
export const COURSE_LESSON_STEPS_INSTRUCTOR: TourStep[] = [
  {
    // Step 2: Modalità Presentazione
    element: '[data-tour-id="btn-presentation-mode"]',
    popover: {
      title: 'Modalità Aula 🎓',
      description: 'Clicca qui per trasformare la lezione in diapositive. Il sistema genererà automaticamente slide pulite per il proiettore basate sui titoli e sulle immagini. Il testo discorsivo viene spostato nelle note del relatore.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    // Step 3: Speaker View & Doppio Monitor
    element: '[data-tour-id="btn-presentation-mode"]',
    popover: {
      title: 'Regia & Proiettore 🖥️',
      description: 'La presentazione apre una finestra di controllo ("Speaker View") con le tue note complete, il timer e l\'anteprima della slide successiva. Separata dalle slide per il pubblico, è ideale per la condivisione su Zoom o il collegamento al proiettore.',
      side: 'bottom',
      align: 'end',
    },
  },
];

/**
 * Compone il tour completo della lezione in base al ruolo utente.
 * Gli step dell'istruttore vengono inseriti dopo il primo step base (sidebar)
 * per un flusso narrativo naturale: Sidebar → Presenta → Speaker → Quiz.
 * 
 * @param isInstructor - Se true, include gli step per la modalità presentazione
 * @returns Array di TourStep composto in base al ruolo
 */
export function buildCourseLessonTour(isInstructor: boolean): TourStep[] {
  if (!isInstructor) {
    return COURSE_LESSON_STEPS_BASE;
  }

  // Inserisci gli step istruttore tra il primo e il secondo step base
  // Ordine: Sidebar → Presentazione → Speaker View → Quiz
  const [sidebarStep, ...restBase] = COURSE_LESSON_STEPS_BASE;
  return [sidebarStep, ...COURSE_LESSON_STEPS_INSTRUCTOR, ...restBase];
}

// ============================================================================
// TOUR: Admin - Gestione
// ============================================================================

/**
 * Tour per l'area di amministrazione
 * Disponibile per utenti con ruolo formatore o superiore
 */
export const ADMIN_TOUR: TourStep[] = [
  {
    element: '[data-tour-id="admin-sections"]',
    popover: {
      title: 'Sezioni di gestione',
      description: 'Da qui puoi accedere a tutte le funzionalità di amministrazione: corsi, utenti, anagrafiche e altro.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour-id="admin-quick-actions"]',
    popover: {
      title: 'Azioni rapide',
      description: 'Accedi velocemente alle operazioni più comuni come creare un nuovo corso o gestire gli utenti.',
      side: 'top',
      align: 'center',
    },
  },
  {
    // Step: Export documentazione PDF
    element: '[data-tour-id="admin-export-docs"]',
    popover: {
      title: 'Esporta Documentazione 📄',
      description: 'Genera un documento tecnico completo del portale in PDF: 51+ rotte suddivise in 10 sezioni tematiche, con livelli di accesso, feature flag e note operative. Utile per presentazioni white-label.',
      side: 'top',
      align: 'center',
    },
  },
];

// ============================================================================
// REGISTRO TOUR
// ============================================================================

/**
 * Mappa di tutti i tour disponibili con i relativi ID
 * Utilizzata dal TourContext per registrare automaticamente i tour
 * 
 * NOTA: Il tour 'course-lesson' non è nel registro perché viene composto
 * dinamicamente da buildCourseLessonTour() in base al ruolo utente.
 * Usa startCustomTour() in CorsoDettaglio.tsx per avviarlo.
 */
export const TOUR_REGISTRY: Record<string, { steps: TourStep[]; title: string; description: string }> = {
  dashboard: {
    steps: DASHBOARD_TOUR,
    title: 'Tour Dashboard',
    description: 'Scopri le funzionalità principali della tua dashboard personale',
  },
  'dashboard-mobile': {
    steps: DASHBOARD_TOUR_MOBILE,
    title: 'Tour Dashboard (Mobile)',
    description: 'Versione compatta del tour per dispositivi mobili',
  },
  course: {
    steps: COURSE_TOUR,
    title: 'Tour Corso',
    description: 'Impara a navigare i contenuti di un corso',
  },
  admin: {
    steps: ADMIN_TOUR,
    title: 'Tour Gestione',
    description: 'Scopri le funzionalità di amministrazione',
  },
};

// ============================================================================
// COSTANTI LOCALSTORAGE
// ============================================================================

/**
 * Chiave localStorage per tenere traccia dei tour già visualizzati
 */
export const TOUR_STORAGE_KEY = 'lni_seen_tours';

/**
 * Verifica se un tour è già stato visualizzato dall'utente
 */
export function hasSeenTour(tourId: string): boolean {
  try {
    const seenTours = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!seenTours) return false;
    const parsed = JSON.parse(seenTours) as string[];
    return parsed.includes(tourId);
  } catch {
    return false;
  }
}

/**
 * Marca un tour come visualizzato
 */
export function markTourAsSeen(tourId: string): void {
  try {
    const seenTours = localStorage.getItem(TOUR_STORAGE_KEY);
    const parsed = seenTours ? JSON.parse(seenTours) as string[] : [];
    if (!parsed.includes(tourId)) {
      parsed.push(tourId);
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch {
    console.warn('[TourSteps] Impossibile salvare lo stato del tour');
  }
}

/**
 * Resetta tutti i tour (utile per testing o riavvio forzato)
 */
export function resetAllTours(): void {
  localStorage.removeItem(TOUR_STORAGE_KEY);
}
